/*
 * helmet-cover-selector.js
 * ────────────────────────
 * Behaviour for the "Add a helmet cover" mini-builder rendered by
 * snippets/helmet-cover-selector.liquid on the ballistic-helmet PDP.
 *
 * Three jobs, all isolated (no edits to the AGB gear-builder engine):
 *   1. Selection UI — None / Multicam / Ranger Green picture squares.
 *   2. Image replace — overlay the chosen cover photo on the large PDP image
 *      (the Prestige gallery carousel + thumbnails are left untouched); "None"
 *      removes the overlay so the helmet's own gallery shows through.
 *   3. Add both — make the gear-builder "Add to cart" ALSO add the matching
 *      cover VARIANT (size inherited from the helmet's selected size) as a
 *      second cart line, and in LAUNCH-PROMO mode a Rear Pouch line too (both
 *      zeroed by a "buy this helmet → get Rear Pouch + Helmet Cover free"
 *      automatic discount in admin — the theme only adds lines, never prices).
 *      We do this by wrapping window.fetch and appending the line(s) to the
 *      engine's OWN cart request — the engine adds to a Storefront cart keyed by
 *      localStorage['AGB_CART_ID'], NOT the Ajax cart, so a naive second
 *      /cart/add.js could land in a different cart. Riding the engine's request
 *      guarantees the add-ons land in the same cart, same batch.
 *
 * Fail-safe: no-ops without the config/UI; every hook is wrapped so it can never
 * break the real add-to-cart.
 */
(function () {
  'use strict';
  if (window.__helmetCoverInstalled) return;
  window.__helmetCoverInstalled = true;

  var cfg = null;
  var selectedColor = null; // null = "None"
  var overlayImg = null;
  var formId = null;

  function readConfig() {
    var el = document.querySelector('[data-helmet-cover-config]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // The set of valid size keys ("Medium"/"Large"/"XL") = the values of the
  // helmet-variant→size map. Computed once.
  var _sizeKeys = null;
  function sizeKeySet() {
    if (_sizeKeys) return _sizeKeys;
    _sizeKeys = {};
    for (var id in cfg.helmetVariantSize) _sizeKeys[cfg.helmetVariantSize[id]] = true;
    return _sizeKeys;
  }

  // The helmet's currently-selected size. Two independent signals, most-reliable
  // first:
  //   1. The SIZE option the shopper actually picked (option1/2/3 whose current
  //      value is a known size key). This is the literal UI choice and needs no
  //      variant-id lookup, so it can't drift from a stale hidden input.
  //   2. Fallback: map the current base variant id (window.baseItem.id — the same
  //      id the gear-builder Add-to-Cart uses — then the form's [name="id"]) via
  //      helmetVariantSize, so the cover size ALWAYS matches the helmet variant
  //      actually being added.
  function helmetSize() {
    var form = formId && document.getElementById(formId);
    var keys = sizeKeySet();
    if (form) {
      var opts = form.querySelectorAll('[name="option1"],[name="option2"],[name="option3"]');
      for (var i = 0; i < opts.length; i++) {
        var el = opts[i];
        if (el.type === 'radio' && !el.checked) continue; // radios: only the checked one
        var val = (el.value || '').trim();
        if (val && keys[val]) return val;                  // direct size match
      }
    }
    var idEl = form && form.querySelector('input[name="id"]');
    var vid = (window.baseItem && window.baseItem.id) || (idEl && idEl.value) || '';
    vid = String(vid).trim();
    return (vid && cfg.helmetVariantSize[vid]) || cfg.defaultSize;
  }

  // The cover variant id for the current colour + helmet size, or null if "None".
  function resolveCoverId() {
    if (!selectedColor || !cfg) return null;
    var color = cfg.colors[selectedColor];
    if (!color) return null;
    var id = color.variants[helmetSize()];
    return id ? Number(id) : null;
  }
  window.__helmetResolveCoverId = resolveCoverId; // read by the fetch appender

  // Every add-on line that should ride the helmet's Add-to-Cart:
  //   • paid (non-promo) mode → just the chosen cover.
  //   • launch-promo mode     → the cover PLUS the matching Rear Pouch, both
  //     zeroed by the "buy this helmet → get Rear Pouch + Helmet Cover free"
  //     automatic discount set up in Shopify admin (the theme only adds lines).
  // Returns [] when no cover colour is picked — the freebie is strictly opt-in,
  // so declining the cover also declines the pouch.
  function resolveAddOnIds() {
    if (promoExpired()) return [];   // deadline passed → add nothing (the discount is gone)
    var coverId = resolveCoverId();
    if (!coverId) return [];
    var ids = [coverId];
    if (cfg && cfg.promoMode && cfg.rearPouchByColor) {
      var pouch = cfg.rearPouchByColor[selectedColor];
      if (pouch) ids.push(Number(pouch));
    }
    return ids;
  }
  window.__helmetResolveAddOnIds = resolveAddOnIds; // read by the fetch appender

  /* ── promo countdown / expiry ───────────────────────────────────────────────
   * The launch offer ends at cfg.promoEndsAt (ISO-8601 with an explicit offset,
   * kept in sync with the Shopify "free with helmet" discount). Before then a
   * countdown shows the time left; at/after it the whole cover promo is HIDDEN
   * and nothing free is added (resolveAddOnIds returns []), so a shopper can
   * never add the now-unpriced cover/pouch once the discount is gone. Active
   * only in promo mode with a valid deadline — paid mode / no-deadline are
   * unaffected. */
  var _deadline = 0;            // ms epoch; 0 = no deadline (never expires)
  function parseDeadline() {
    if (!cfg || !cfg.promoMode || !cfg.promoEndsAt) return 0;
    var t = new Date(cfg.promoEndsAt).getTime();
    return isNaN(t) ? 0 : t;
  }
  function promoExpired() { return _deadline > 0 && Date.now() >= _deadline; }
  function hidePromoCard() {
    var root = document.querySelector('[data-helmet-cover]');
    if (root) root.style.display = 'none';
    try { deselectAll(); } catch (e) {}   // clear any selected cover + the gallery overlay
  }

  /* ── image overlay ─────────────────────────────────────────────────────── */
  function ensureOverlay() {
    if (overlayImg) return overlayImg;
    var host = document.querySelector('.product-gallery__image-list') ||
               document.querySelector('.product-gallery');
    if (!host) return null;
    overlayImg = document.createElement('img');
    overlayImg.className = 'helmet-cover-overlay';
    overlayImg.alt = 'Helmet with cover preview';
    overlayImg.hidden = true;
    host.appendChild(overlayImg);
    return overlayImg;
  }
  function showCoverImage(url) {
    var img = ensureOverlay();
    if (!img) return;
    img.src = url;
    img.hidden = false;
    // next frame so the opacity transition runs
    requestAnimationFrame(function () { img.classList.add('is-visible'); });
  }
  function hideCoverImage() {
    if (!overlayImg) return;
    overlayImg.classList.remove('is-visible');
    overlayImg.hidden = true;
  }

  /* ── selection ─────────────────────────────────────────────────────────── */
  function select(color, tileEl) {
    selectedColor = color || null;
    var tiles = document.querySelectorAll('[data-cover-tile]');
    Array.prototype.forEach.call(tiles, function (t) {
      var on = t === tileEl;
      t.classList.toggle('is-selected', on);
      t.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    if (selectedColor && cfg.colors[selectedColor]) showCoverImage(cfg.colors[selectedColor].image);
    else hideCoverImage();
  }

  // No "None" tile — deselect by clicking the already-selected tile. Clears the
  // selection => no cover added + original PDP image restored.
  function deselectAll() {
    selectedColor = null;
    Array.prototype.forEach.call(document.querySelectorAll('[data-cover-tile]'), function (t) {
      t.classList.remove('is-selected');
      t.setAttribute('aria-pressed', 'false');
    });
    hideCoverImage();
  }

  /* ── add-both: append the cover line to the engine's own cart request ────── */
  function installFetchAppender() {
    if (typeof window.fetch !== 'function') return;
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      try {
        var ids = resolveAddOnIds(); // cover (+ Rear Pouch in promo mode)
        var url = (typeof input === 'string' ? input : (input && input.url)) || '';
        if (ids.length && init && (init.method || '').toUpperCase() === 'POST' && typeof init.body === 'string') {
          var body = JSON.parse(init.body);
          var changed = false;
          // A) Storefront Cart API (primary): cartLinesAdd mutation
          if (body.query && /cartLinesAdd/.test(body.query) && body.variables && Array.isArray(body.variables.lines)) {
            ids.forEach(function (id) {
              var gid = 'gid://shopify/ProductVariant/' + id;
              if (!body.variables.lines.some(function (l) { return l.merchandiseId === gid; })) {
                body.variables.lines.push({ quantity: 1, merchandiseId: gid });
                changed = true;
              }
            });
          // B) Ajax fallback: POST /cart/add.js with { items: [...] }
          } else if (/\/cart\/add(\.js)?($|\?)/.test(url) && Array.isArray(body.items)) {
            ids.forEach(function (id) {
              if (!body.items.some(function (it) { return Number(it.id) === id; })) {
                body.items.push({ id: id, quantity: 1 });
                changed = true;
              }
            });
          }
          if (changed) init = Object.assign({}, init, { body: JSON.stringify(body) });
        }
      } catch (e) { /* never break the real add-to-cart */ }
      return nativeFetch(input, init);
    };
  }

  function init() {
    cfg = readConfig();
    if (!cfg) return;
    var root = document.querySelector('[data-helmet-cover]');
    if (!root) return;

    // Promo already over on load → hide the whole cover card and add nothing.
    _deadline = parseDeadline();
    if (promoExpired()) { root.style.display = 'none'; return; }

    formId = window.AGB_FORM_ID ||
      (document.querySelector('form[action*="/cart/add"]') || {}).id || null;

    Array.prototype.forEach.call(root.querySelectorAll('[data-cover-tile]'), function (tile) {
      tile.addEventListener('click', function () {
        // Toggle: clicking the already-selected tile deselects it (no cover).
        if (tile.classList.contains('is-selected')) {
          deselectAll();
        } else {
          select(tile.getAttribute('data-cover-color') || null, tile);
        }
      });
    });

    installFetchAppender();
    // Live countdown via the reusable promo timer (promo-timer.js, styled after
    // the Fanny↔CCW promo). When it hits 0 it removes itself and onExpire hides
    // the whole free-offer card; resolveAddOnIds() also returns [] past the
    // deadline, so nothing free can be added once the offer ends.
    if (_deadline > 0 && window.AGBPromoTimer) {
      var timerHost = document.querySelector('[data-promo-timer]');
      if (timerHost) window.AGBPromoTimer.mount(timerHost, {
        endsAt: _deadline,
        label: 'ends in',
        variant: 'plain',           // no red bar — the Fanny↔CCW promo-card look (Dorian 2026-07-15)
        onExpire: hidePromoCard
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
